import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, ChevronRight, Check } from "lucide-react";
import api, { formatPrice, mediaUrl, track } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import ProductCard from "../components/ProductCard";

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 py-2.5 border-b border-linen/70 text-sm">
      <span className="text-stonemuted">{label}</span>
      <span className="text-charcoal text-right font-medium">{value}</span>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, inWishlist, inCart } = useStore();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);
  const [zoom, setZoom] = useState(null);
  const imgRef = useRef(null);

  useEffect(() => {
    setNotFound(false); setProduct(null);
    (async () => {
      try {
        const { data } = await api.get(`/products/${slug}`);
        setProduct(data);
        setActive(0);
        document.title = `${data.brand} ${data.title} — LUXYREIGN`;
        track("product_view", { product_id: data.id });
        const rel = await api.get("/products", { params: { brand: data.brand, limit: 5 } });
        setRelated(rel.data.products.filter((p) => p.id !== data.id).slice(0, 4));
      } catch { setNotFound(true); }
    })();
    return () => { document.title = "LUXYREIGN — Timeless Luxury. Reimagined."; };
  }, [slug]);

  if (notFound) return (
    <div className="luxy-container py-32 text-center">
      <p className="eyebrow mb-3">404</p>
      <h1 className="font-serif text-4xl text-pitch">Piece not found</h1>
      <p className="text-stonemuted mt-3">This item may have been sold or removed from the archive.</p>
      <Link to="/shop" className="inline-block mt-8 text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1">Return to Shop</Link>
    </div>
  );
  if (!product) return <div className="luxy-container py-32 text-center font-serif text-2xl tracking-[0.3em] text-stonemuted animate-pulse">LUXYREIGN</div>;

  const sold = product.availability_status === "sold" || product.availability_status === "reserved";
  const images = (product.images || []).map(mediaUrl);
  const wished = inWishlist(product.id);

  const onMove = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoom({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  return (
    <div className="luxy-container py-8 sm:py-12">
      <nav className="flex items-center gap-2 text-xs text-stonemuted mb-8 font-mono">
        <Link to="/shop" className="hover:text-pitch">SHOP</Link><ChevronRight className="w-3 h-3" />
        <Link to={`/shop?brand=${encodeURIComponent(product.brand)}`} className="hover:text-pitch uppercase">{product.brand}</Link>
        <ChevronRight className="w-3 h-3" /><span className="text-charcoal truncate max-w-[160px]">{product.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_460px] gap-8 lg:gap-16">
        {/* Gallery */}
        <div>
          <div ref={imgRef} onMouseMove={onMove} onMouseLeave={() => setZoom(null)}
            className="relative bg-bone aspect-[4/5] overflow-hidden zoom-lens" data-testid="pdp-gallery-main-image">
            {images[active] && <img src={images[active]} alt={`${product.brand} ${product.title}`}
              className="w-full h-full object-cover" style={zoom ? { transform: "scale(2)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}} />}
            {sold && <div className="absolute inset-0 bg-pitch/45 flex items-center justify-center">
              <span className="text-sm font-mono tracking-[0.35em] uppercase text-white border border-white/60 px-6 py-2.5 bg-pitch/70">Sold</span>
            </div>}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-3 mt-3">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid="pdp-thumbnail-item"
                  className={`aspect-square bg-bone overflow-hidden border ${active === i ? "border-pitch" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Dossier */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className="text-xs font-mono uppercase tracking-[0.22em] text-stonemuted">{product.brand}</p>
          <h1 className="font-serif font-light text-3xl sm:text-4xl text-pitch leading-tight mt-2">{product.title}</h1>
          <div className="flex items-baseline gap-3 mt-5">
            <span className="text-2xl font-medium text-pitch">{formatPrice(product.price, product.currency)}</span>
            {product.original_retail_price && <span className="text-sm line-through text-stonemuted">Retail {formatPrice(product.original_retail_price, product.currency)}</span>}
          </div>

          <div className="flex flex-wrap gap-2 mt-5">
            <span className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 bg-champagne-soft border border-champagne/40 text-charcoal">{product.condition}</span>
            {product.inventory_quantity === 1 && !sold && <span className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 bg-pitch text-ivory">One of One</span>}
            {product.authentication_public && product.authentication_status && (
              <span className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 bg-emeraldauth/10 border border-emeraldauth/30 text-emeraldauth inline-flex items-center gap-1.5">
                <Check className="w-3 h-3" /> {product.authentication_status}
              </span>
            )}
          </div>

          {product.description && <p className="text-sm text-charcoal/80 leading-relaxed mt-6">{product.description}</p>}

          <div className="mt-8 space-y-3">
            {!sold ? (
              <button onClick={() => { addToCart(product); }} disabled={inCart(product.id)}
                data-testid="pdp-add-to-bag-button"
                className="w-full bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] py-4 hover:bg-charcoal transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                {inCart(product.id) ? "In Your Bag" : "Add to Bag"}
              </button>
            ) : (
              <button disabled data-testid="pdp-sold-button"
                className="w-full bg-stonemuted/30 text-charcoal text-xs font-mono uppercase tracking-[0.2em] py-4 cursor-not-allowed">
                Archived Piece — Sold
              </button>
            )}
            <button onClick={() => toggleWishlist(product)} data-testid="pdp-wishlist-toggle"
              className="w-full inline-flex items-center justify-center gap-2 border border-pitch text-pitch text-xs font-mono uppercase tracking-[0.2em] py-4 hover:bg-pitch hover:text-ivory transition-colors">
              <Heart className={`w-4 h-4 ${wished ? "fill-current" : ""}`} /> {wished ? "Saved to Wishlist" : "Add to Wishlist"}
            </button>
          </div>

          {/* Details */}
          <div className="mt-8">
            <Row label="Brand" value={product.brand} />
            <Row label="Model" value={product.model} />
            <Row label="Condition" value={product.condition} />
            <Row label="Colour" value={product.color} />
            <Row label="Material" value={product.material} />
            <Row label="Hardware" value={product.hardware} />
            <Row label="Dimensions" value={product.dimensions} />
            <Row label="Year" value={product.year} />
            <Row label="SKU" value={product.sku} />
          </div>

          <Accordion type="single" collapsible className="mt-6" data-testid="pdp-accordions">
            {product.condition_notes && (
              <AccordionItem value="condition" className="border-linen">
                <AccordionTrigger className="text-xs font-mono uppercase tracking-[0.15em]" data-testid="pdp-condition-report">Condition Report</AccordionTrigger>
                <AccordionContent className="text-sm text-charcoal/80 leading-relaxed">{product.condition_notes}</AccordionContent>
              </AccordionItem>
            )}
            <AccordionItem value="auth" className="border-linen">
              <AccordionTrigger className="text-xs font-mono uppercase tracking-[0.15em]">Authentication</AccordionTrigger>
              <AccordionContent className="text-sm text-charcoal/80 leading-relaxed space-y-1">
                {product.authentication_public ? (
                  <>
                    <div>Status: {product.authentication_status}</div>
                    {product.authentication_method && <div>Method: {product.authentication_method}</div>}
                    {product.authentication_provider && <div>Provider: {product.authentication_provider}</div>}
                    {product.certificate_available && <div>Certificate: {product.certificate_type}</div>}
                  </>
                ) : (
                  <p>Authentication details for this piece have not been published. Please contact our concierge for more information.</p>
                )}
              </AccordionContent>
            </AccordionItem>
            {product.included_accessories?.length > 0 && (
              <AccordionItem value="accessories" className="border-linen">
                <AccordionTrigger className="text-xs font-mono uppercase tracking-[0.15em]">Included Accessories</AccordionTrigger>
                <AccordionContent className="text-sm text-charcoal/80">{product.included_accessories.join(", ")}</AccordionContent>
              </AccordionItem>
            )}
            <AccordionItem value="shipping" className="border-linen">
              <AccordionTrigger className="text-xs font-mono uppercase tracking-[0.15em]">Shipping & Returns</AccordionTrigger>
              <AccordionContent className="text-sm text-charcoal/80 leading-relaxed">
                Dispatched via insured express courier. Delivery and returns terms are shown at checkout and on our policy pages.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <h2 className="font-serif text-2xl sm:text-3xl text-pitch mb-8">More from {product.brand}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
